import shaka from 'shaka-player/dist/shaka-player.ui'
import 'shaka-player/dist/controls.css'
import { getLogger } from './logger'

const log = getLogger('VideoPlayer')

export type VideoFormat = 'dash' | 'hls' | 'mp4'

export const ShakaErrorCode = shaka.util.Error.Code
export type ShakaError = shaka.util.Error
export type VideoPlayerError = ShakaError | Error

export interface VideoPlayerConfig {
  id?: string
  url: string
  format?: VideoFormat
  autoplay?: boolean
  loop?: boolean
  muted?: boolean
  controls?: boolean
  poster?: string
  /**
   * When true, disables ABR and locks to a fixed quality track.
   * Used for local playback mode to ensure consistent caching.
   */
  disableAbr?: boolean
}

export interface VideoPlayerCallbacks {
  onError?: (error: VideoPlayerError) => void
  onLoading?: (loading: boolean) => void
  onReady?: () => void
  onCacheableUrls?: (urls: string[]) => void
}

interface InternalVideoPlayerCallbacks extends VideoPlayerCallbacks {
  onDestroyed?: () => void
}

export class VideoPlayer {
  private player: shaka.Player | null = null
  private ui: shaka.ui.Overlay | null = null
  private videoElement: HTMLVideoElement | null = null
  private destroyPromise: Promise<void> | null = null

  constructor(
    public id: string,
    private containerElement: HTMLDivElement,
    private config: VideoPlayerConfig,
    private callbacks: InternalVideoPlayerCallbacks = {}
  ) {
    log(`VideoPlayer constructor ${id}`, config)
  }

  static isBrowserSupported(): boolean {
    return shaka.Player.isBrowserSupported()
  }

  // Detect video format based on URL
  static detectFormat(url: string): VideoFormat {
    const lowerUrl = url.toLowerCase()
    if (lowerUrl.includes('.mpd')) return 'dash'
    if (lowerUrl.includes('.m3u8')) return 'hls'
    return 'mp4'
  }

  async initialize(): Promise<void> {
    try {
      // Find video element in container
      this.videoElement = this.containerElement.querySelector('video')
      if (!this.videoElement) {
        throw new Error('No video element found in container')
      }

      // Create Shaka Player instance
      this.player = new shaka.Player()

      // Attach to video element
      await this.player.attach(this.videoElement)

      const abrEnabled = !this.config.disableAbr

      if (!abrEnabled) {
        log('ABR disabled for local playback mode')
      }

      // Configure player settings
      this.player.configure({
        streaming: {
          bufferingGoal: 30, // Buffer ahead
          rebufferingGoal: 5, // Buffer at least this many seconds before playing
          bufferBehind: 10, // Buffer behind current playhead (not important for our use case because we do no seek backwards)
        },
        abr: {
          enabled: abrEnabled,
        },
      })

      // Setup error handling
      this.player.addEventListener('error', this.handleError.bind(this))

      // Setup video event listeners for loading states
      this.setupVideoEventListeners()

      // Setup UI controls if enabled
      if (this.config.controls) {
        this.setupUI()
      }

      if (this.config.muted) {
        this.setMuted(true)
      }

      // Setup loop handler
      if (this.config.loop) {
        this.setupLoopHandler()
      }
    } catch (error: any) {
      log('Failed to initialize VideoPlayer:', error)
      this.callbacks.onError?.(error)
      throw error
    }
  }

  async load(): Promise<void> {
    if (!this.player) {
      throw new Error('Player not initialized. Call initialize() first.')
    }

    try {
      this.callbacks.onLoading?.(true)

      const { url, format = VideoPlayer.detectFormat(url) } = this.config

      // For MP4, use native video element
      if (format === 'mp4') {
        this.videoElement!.src = url
      } else {
        // For DASH and HLS, use Shaka Player
        await this.player.load(url)

        // When ABR is disabled, select highest quality track for caching
        if (this.config.disableAbr) {
          const targetTrack = this.selectHighestQualityTrack()
          this.extractSegmentUrls(targetTrack)
        }
      }

      // Autoplay if enabled
      if (this.config.autoplay) {
        await this.play()
      }
    } catch (error: any) {
      log('Failed to load video:', error)
      this.callbacks.onLoading?.(false)
      this.callbacks.onError?.(error)
      throw error
    }
  }

  async play(): Promise<void> {
    if (!this.videoElement) return
    try {
      await this.videoElement.play()
    } catch (error: any) {
      // Autoplay might fail due to browser policies
      console.warn('Autoplay failed:', error)
      // Don't throw error for autoplay failures
    }
  }

  pause(): void {
    if (!this.videoElement) return
    this.videoElement.pause()
  }

  seek(time: number): void {
    if (!this.videoElement) return
    this.videoElement.currentTime = time
  }

  getCurrentTime(): number {
    return this.videoElement?.currentTime || 0
  }

  getDuration(): number {
    return this.videoElement?.duration || 0
  }

  // Between 0.0 and 1.0
  setVolume(volume: number): void {
    if (!this.videoElement) return
    this.videoElement.volume = Math.max(0, Math.min(1, volume))
  }

  setMuted(muted: boolean): void {
    if (!this.videoElement) return
    this.videoElement.muted = muted
  }

  isPlaying(): boolean {
    if (!this.videoElement) return false
    return !this.videoElement.paused && !this.videoElement.ended
  }

  private setupVideoEventListeners(): void {
    if (!this.videoElement) return

    // When video starts playing
    this.videoElement.addEventListener('playing', () => {
      this.callbacks.onLoading?.(false)
      this.callbacks.onReady?.()
    })

    // When video is buffering/waiting
    this.videoElement.addEventListener('waiting', () => {
      this.callbacks.onLoading?.(true)
    })

    // When video has enough data to play
    this.videoElement.addEventListener('canplay', () => {
      this.callbacks.onLoading?.(false)
    })
  }

  private setupUI(): void {
    if (!this.player) return

    this.ui = new shaka.ui.Overlay(this.player, this.containerElement, this.videoElement!)

    const uiConfig = {
      addSeekBar: true,
      addBigPlayButton: true,
      controlPanelElements: ['play_pause', 'time_and_duration', 'spacer', 'mute', 'volume', 'fullscreen'],
    }

    this.ui.configure(uiConfig)
  }

  private setupLoopHandler(): void {
    if (!this.videoElement) return

    const handleEnded = () => {
      if (this.config.loop && this.videoElement) {
        this.videoElement.currentTime = 0
        this.play().catch((err) => {
          log('Error replaying video in loop:', err)
        })
      }
    }

    this.videoElement.addEventListener('ended', handleEnded)
  }

  private handleError(event: any): void {
    const errorDetail = event.detail
    log('Shaka Player Error:', errorDetail)

    this.callbacks.onError?.(errorDetail ?? event)
  }

  private selectHighestQualityTrack(): shaka.extern.Track | null {
    if (!this.player) return null

    const tracks = this.player.getVariantTracks()
    if (tracks.length === 0) {
      log('No variant tracks available')
      return null
    }

    // Sort by height descending, pick highest
    const sorted = [...tracks].sort((a, b) => (b.height ?? 0) - (a.height ?? 0))
    const highest = sorted[0]

    const resolutions = sorted.map((t) => `${t.width}x${t.height}`).join(', ')

    log('Local playback: available resolutions: %s', resolutions)
    log(
      'Local playback: selecting highest quality: %dx%d (%dkbps)',
      highest.width,
      highest.height,
      Math.round((highest.bandwidth ?? 0) / 1000)
    )

    // Skip if already on the highest track
    if (!highest.active) {
      // Switch quality at the next segment boundary without clearing the buffer.
      // clearBuffer: true can cause the MediaSource to close prematurely on some
      // WebKit builds (e.g. Safari 17 / Sonoma), triggering the 'ended' event
      // and a playback restart loop.
      const clearBuffer = false
      this.player.selectVariantTrack(highest, clearBuffer)
    } else {
      log('Local playback: already on highest quality, no switch needed')
    }

    return highest
  }

  private async extractSegmentUrls(preferredTrack?: shaka.extern.Track | null): Promise<void> {
    if (!this.player || !this.callbacks.onCacheableUrls) return

    try {
      const manifest = this.player.getManifest()
      if (!manifest) {
        log('No manifest available for segment extraction')
        return
      }

      // Prefer the explicitly selected track to avoid timing races after
      // selectVariantTrack(..., false), where active track may lag briefly.
      const targetTrack = preferredTrack ?? this.player.getVariantTracks().find((t) => t.active)
      if (!targetTrack) {
        log('No target variant track found')
        return
      }

      // Find the matching video stream in the manifest
      const videoStream = manifest.variants.find(
        (v: any) => v.video?.height === targetTrack.height && v.video?.width === targetTrack.width
      )?.video

      if (!videoStream) {
        log('Could not find matching video stream in manifest')
        return
      }

      // Create segment index if not already created
      await videoStream.createSegmentIndex()

      const segmentIndex = videoStream.segmentIndex
      if (!segmentIndex) {
        log('No segment index available')
        return
      }

      const urls: string[] = []

      // Iterate segment references
      for (const ref of segmentIndex) {
        if (!ref) continue

        // Extract init segment URL from the first segment reference
        // (initSegmentReference exists at runtime but isn't in Shaka's type definitions)
        if (urls.length === 0) {
          const initRef = (ref as any).initSegmentReference
          if (initRef?.getUris) {
            const initUris = initRef.getUris()
            if (initUris.length > 0) {
              urls.push(initUris[0])
            }
          }
        }

        const segUris = ref.getUris()
        if (segUris.length > 0) {
          urls.push(segUris[0])
        }
      }

      log('Extracted %d segment URLs for track %dx%d', urls.length, targetTrack.width, targetTrack.height)
      this.callbacks.onCacheableUrls(urls)
    } catch (error) {
      log('Failed to extract segment URLs:', error)
    }
  }

  async _destroy(): Promise<void> {
    try {
      this.ui = null

      await this.player?.destroy()
      this.player = null
      this.callbacks.onDestroyed?.()

      log('VideoPlayer destroyed successfully %s', this.id)
    } catch (error) {
      log('Error destroying VideoPlayer:', error)
    }
  }

  async destroy() {
    this.destroyPromise = this._destroy()
    return this.destroyPromise
  }

  isDestroying(): boolean {
    return !!this.destroyPromise
  }

  async waitUntilDestroyed() {
    if (!this.isDestroying()) {
      throw new Error('Video player is not destroying!')
    }
    return this.destroyPromise
  }

  getStats(): any {
    return this.player?.getStats() || null
  }

  getBufferedInfo(): any {
    return this.player?.getBufferedInfo() || null
  }

  isLive(): boolean {
    return this.player?.isLive() ?? false
  }

  // live streams may restrict seeking
  isSeekable(): boolean {
    const seekRange = this.player?.seekRange()
    if (!seekRange) return false
    return seekRange.end - seekRange.start > 0
  }

  getSeekRange(): { start: number; end: number } | null {
    return this.player?.seekRange() || null
  }
}
